// askSmart-ui/src/pages/TOSP/components/BuildingModal.tsx
import React, { useEffect, useState } from "react";
import { Building } from "../types";
import { requiredText, sameText } from "../validation";

export interface BuildingFormValues {
  name: string;
  nickname: string;
  gsccNumber: string;
}

interface BuildingModalProps {
  isOpen: boolean;
  /** The building being edited, or null when adding a new one. */
  building: Building | null;
  /** Every building, so the name-uniqueness check can see its siblings. */
  buildings: Building[];
  onClose: () => void;
  /** Awaited, so the button can hold until the write is done. */
  onSave: (values: BuildingFormValues) => void | Promise<void>;
}

// Add / edit form for a single Building. One modal serves both, so the fields
// and validation can never drift between the two paths.
export default function BuildingModal({
  isOpen,
  building,
  buildings,
  onClose,
  onSave,
}: BuildingModalProps) {
  const [name, setName] = useState("");
  const [nickname, setNickname] = useState("");
  const [gsccNumber, setGsccNumber] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // Reload the form whenever it opens, so a cancelled edit leaves nothing
  // behind for the next Add.
  useEffect(() => {
    if (!isOpen) return;
    setName(building?.name || "");
    setNickname(building?.nickname || "");
    setGsccNumber(building?.gsccNumber || "");
    setErrors({});
    setBusy(false);
  }, [isOpen, building]);

  if (!isOpen) return null;

  const validate = (): { values?: BuildingFormValues; errors: Record<string, string> } => {
    const next: Record<string, string> = {};
    const trimmedName = name.trim();

    const nameError = requiredText(name, "Building Name");
    if (nameError) {
      next.name = nameError;
    } else if (buildings.some((b) => b.id !== building?.id && sameText(b.name, trimmedName))) {
      next.name = "A building with this name already exists.";
    }

    if (Object.keys(next).length > 0) return { errors: next };
    return {
      errors: next,
      values: {
        name: trimmedName,
        nickname: nickname.trim(),
        gsccNumber: gsccNumber.trim(),
      },
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const { values, errors: found } = validate();
    setErrors(found);
    if (!values) return;

    setBusy(true);
    try {
      await onSave(values);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "480px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            {building ? "Edit Building" : "Add Building"}
          </h2>
        </div>
        {/* autoComplete="off" on the form and every field: these are property
            records, not the user's own details, so the browser's saved-value
            dropdown is noise. */}
        <form className="smrtdocs-modal-body" onSubmit={handleSubmit} noValidate autoComplete="off">
          <div style={{ marginBottom: 16 }}>
            <label className="tosp-label" htmlFor="tosp-building-name">
              Building Name <span className="tosp-required">*</span>
            </label>
            <input
              id="tosp-building-name"
              className="manage-input"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            {errors.name && <p className="tosp-field-error">{errors.name}</p>}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="tosp-label" htmlFor="tosp-building-nickname">
              Nickname
            </label>
            <input
              id="tosp-building-nickname"
              className="manage-input"
              autoComplete="off"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="tosp-label" htmlFor="tosp-building-gscc">
              GSCC #
            </label>
            <input
              id="tosp-building-gscc"
              className="manage-input"
              autoComplete="off"
              value={gsccNumber}
              onChange={(e) => setGsccNumber(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button type="button" className="btn-cancel" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? (
                <>
                  <span className="tosp-spinner" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
